import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Grid2, CircularProgress, Breadcrumbs, Link } from "@mui/material";
import styles from "./CreateOrgStyles.module.scss";
import { useSelector } from "react-redux";
import zipcodes from "zipcodes";

// Local imports
import CustomizedSteppers from "../../Components/Stepper/Stepper";
import CustomInput from "../../../../../../GlobalComponents/CustomInput/CustomInput";
import Dropdown from "../../../../../../GlobalComponents/Dropdown/Dropdown";
import ReviewField from "../../../../../../GlobalComponents/ReviewField/ReviewField";
import ReviewCard from "../../../../../../GlobalComponents/ReviewCard/ReviewCard";
import ProcessingPage from "../../../CreateSite/Components/ProcessingPage/ProcessingPage";
import {
  getRoleDisplayText,
  INVITE_ROLES,
} from "../../../../../../Utils/Roles";
import FormPopup from "../../../../../../GlobalComponents/FormPopup/FormPopup";

// Icons
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { ReactComponent as TempIcon } from "../../../../AdminAssets/svg/BlankSite.svg";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import { ReactComponent as DeleteIcon } from "../../../../../../GlobalAssets/svg/Delete.svg";
import { Actions } from "../../../../../../redux/constants/actionTypes";
import { getStore } from "../../../../../../Utils/storeUtils";
import { useMakeApiCalls } from "../../../../../../Hooks/useMakeApiCalls";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { getRoleInOrg } from "../../../../../../Utils/organizations";
import BulkInvite from "../BulkInvite/BulkInvite";
import NewPopup from "../../../../../../GlobalComponents/Popup/NewPopup";
import { DropdownStyles } from "../../../../../../GlobalComponents/Dropdown";
import { useEmailId, verifyEmail } from "../../../../../../Utils/utils";
import { sassTrue } from "sass";

const steps = [
  {
    title: "Organization Details",
    //subtitle: "Fill in your organization details",
    icon: <CorporateFareIcon />,
  },
  {
    title: "Invite Member",
    //subtitle: "Get your projects up and running faster by inviting",
    icon: <AccountCircleIcon />,
  },
  {
    title: "Review & Finish",
    //subtitle: "Confirm your details and finish",
    icon: <AssignmentTurnedInIcon />,
  },
];

export default function CreateOrganization(props) {
  const { clientOrganization, onBackToOptions } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const emailId = useEmailId();
  const [popup, setPopup] = useState({
    popupState: false,
    popupType: "",
    popupTitle: "",
    popupSubtitle: "",
    popupSubtitle1: "",
    popupButton: false,
    popupCancel: false,
    popupButtonTitle: "",
    popupButtonFunction: () => {},
    popupCancelTitle: "",
    popupCancelFunction: () => {},
  });
  const [activeStep, setActiveStep] = useState(0);
  const [isloading, setIsLoading] = useState(false);
  const [orgForm, setOrgForm] = useState({
    orgName: "",
    orgNameError: false,
    orgNameErrorMessage: "",
    address: "",
    city: "",
    state: "",
    ...(clientOrganization
      ? {}
      : {
          zipCode: "",
          zipCodeError: false,
          zipCodeErrorMessage: null,
          country: "USA",
        }),
    members: [{ memberEmailId: "", roleId: "" }],
    memberError: [
      {
        memberEmailIdError: false,
        roleIdError: false,
        memberEmailIdErrorMessage: false,
      },
    ],
    selectOrganization: {},
  });

  const [selectedOrgCountry, setSelectedOrgCountry] = useState("");
  const [selectedOrg, setSelectedOrg] = useState("");
  const [nameLoading, setNameLoading] = useState(false);
  const [nameValid, setNameValid] = useState(null);
  const [bulkInvite, setBulkInvite] = useState(false);
  const [tempBulkData, setTempBulkData] = useState([]);
  const [createOrgFormBulk, setCreateOrgFormBulk] = useState([]);
  const [bulkData, setBulkData] = useState([]);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkFileName, setBulkFileName] = useState("");
  const [bulkFileSize, setBulkFileSize] = useState("");
  const [redundantEmails, setRedundantEmails] = useState([]);
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const [isBulkDisabled, setIsBulkDisabled] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [errors, setErrors] = useState([]);
  const selectedOrgId = location.state?.selectedOrgId;

  //data
  const organizationsInfo = useSelector((state) => state.organizationsInfo);
  const orgUsers = organizationsInfo?.userOrgs?.data;
  const orgData = organizationsInfo?.dashboardOrg?.data;
  let userDetails = useSelector((state) => state.users.userDetails?.theme);
  const childOrgData =
    orgData?.filter(
      (org) =>
        org.parentOrgName === null &&
        org.planName !== null &&
        org.planName.startsWith("MS"),
    ) || [];

  const parentOrg = childOrgData?.find(
    (org) => org.orgId === parseInt(selectedOrgId),
  );
  useEffect(() => {
    if (parentOrg) {
      setOrgForm((prevState) => ({
        ...prevState,
        selectOrganization: {
          value: parentOrg.orgId,
          label: parentOrg.name,
        },
      }));
    }
  }, [parentOrg]);
  //dropdownmoptions data
  const countryOptions = [
    { label: "United States of America", value: "USA" },
    { label: "India", value: "India" },
  ];

  const roleOptions = INVITE_ROLES.map((role) => ({
    value: role.roleId,
    label: role.displayName,
  }));

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const isValidEmail = useCallback((memberEmailId) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(memberEmailId);
  }, []);

  //calling api's
  useMakeApiCalls(() => {
    if (clientOrganization) {
      getStore().dispatch({ type: Actions.API_ORG_DASHBOARD });
      getStore().dispatch({ type: Actions.API_USER_ORGS });
    }
  }, []);

  useEffect(() => {
    isValidEmail();
  }, [isValidEmail]);

  // to get the country and other options of selected organization for creating client org
  useEffect(() => {
    if (parseInt(orgForm.selectOrganization.value, 10)) {
      const selectedOrg = orgUsers?.find(
        (item) =>
          item.orgId === orgForm.selectOrganization.value &&
          orgForm.selectOrganization.label,
      );

      if (selectedOrg) {
        const selectedOrgCountry = selectedOrg.country;

        // Set the selected organization details if found
        setSelectedOrg(selectedOrg);
        setSelectedOrgCountry(selectedOrgCountry);
      }
    }
  }, [
    orgForm.selectOrganization.value,
    orgForm.selectOrganization.label,
    orgUsers,
  ]);

  //check if given org name is valid or not
  useEffect(() => {
    if (orgForm.orgName?.length >= 3) {
      const orgName = orgForm.orgName.toLowerCase().trim();

      const myTimeout = setTimeout(() => {
        if (orgName.length >= 3) {
          setNameLoading(true);
          getStore().dispatch({
            type: Actions.API_IS_VALID_ORG,
            orgName: orgName,
            meta: {
              onSuccess: () => {
                setNameLoading(false);
                setNameValid(true);
              },
              onError: () => {
                setNameLoading(false);
                setNameValid(false);
                setOrgForm((prev) => ({
                  ...prev,
                  orgNameError: true,
                  orgNameErrorMessage: "Organization already exists",
                }));
              },
            },
          });
        } else {
          setOrgForm((prev) => ({
            ...prev,
            orgNameError: null,
            orgNameErrorMessage: null,
          }));
        }
      }, 500);

      return () => {
        clearTimeout(myTimeout);
      };
    }
  }, [orgForm.orgName]);

  useEffect(() => {
    const hasDuplicateEmails = (entries) => {
      const emailSet = new Set();
      for (const entry of entries) {
        if (emailSet.has(entry.memberEmailId)) {
          return true; // Duplicate found
        }
        emailSet.add(entry.memberEmailId);
      }
      return false;
    };

    const isEmailIdInBulk = createOrgFormBulk.some(
      (entry) => entry.memberEmailId === emailId,
    );

    const isBulkInvalid =
      tempBulkData?.length === 0 ||
      tempBulkData.some(
        (entry) =>
          !entry.memberEmailId ||
          !isValidEmail(entry.memberEmailId) ||
          !entry.roleId ||
          ![3, 4, 5].includes(entry.roleId),
      ) ||
      hasDuplicateEmails(tempBulkData) ||
      isEmailIdInBulk;

    setIsNextDisabled(tempBulkData?.length > 0 && isBulkInvalid);
    setIsBulkDisabled(isBulkInvalid);
  }, [tempBulkData]);

  const checkAllMemberEmails = () => {
    const updatedMemberErrors = orgForm.members.map((member, idx) => {
      const emailValid = verifyEmail(member.memberEmailId);
      return {
        ...orgForm.memberError[idx],
        memberEmailIdError: member.memberEmailId ? !emailValid : false,
        memberEmailIdErrorMessage:
          member.memberEmailId && !emailValid ? "Invalid email" : "",
      };
    });

    setOrgForm((prevState) => ({
      ...prevState,
      memberError: updatedMemberErrors,
    }));

    return updatedMemberErrors.some((err) => err.memberEmailIdError);
  };

  const clientOrganizationHasErrors = () => {
    const hasOrgNameError = !orgForm.orgName || orgForm.orgNameError;
    const hasSelectOrganizationError = !orgForm.selectOrganization.value;

    const hasDuplicateEmails =
      orgForm.members?.length > 0 &&
      orgForm.members.some((member, index) =>
        orgForm.members
          .slice(index + 1)
          .some(
            (otherMember) => otherMember.memberEmailId === member.memberEmailId,
          ),
      );

    const hasMemberErrors = orgForm.members.some((member) => {
      const emailHasValue = !!member.memberEmailId;
      const isEmailValid =
        emailHasValue && emailRegex.test(member.memberEmailId);
      const roleMissing = !member.roleId;

      return emailHasValue && (!isEmailValid || roleMissing);
    });

    const hasBulkErrors = tempBulkData.some((entry) => {
      const emailHasValue = !!entry.memberEmailId;
      const isEmailValid =
        emailHasValue && emailRegex.test(entry.memberEmailId);
      const roleMissing = !entry.roleId;

      return emailHasValue && (!isEmailValid || roleMissing);
    });

    return (
      hasOrgNameError ||
      hasSelectOrganizationError ||
      hasDuplicateEmails ||
      hasMemberErrors ||
      hasBulkErrors || // Include bulk errors check
      isNextDisabled
    );
  };

  const hasErrors = () => {
    const zipCodeValid = validateZipCode(orgForm.zipCode, orgForm.country);
    const hasOrgNameError = !orgForm.orgName || orgForm.orgNameError;

    const hasDuplicateEmails =
      orgForm.members?.length > 0 &&
      orgForm.members.some((member, index) =>
        orgForm.members
          .slice(index + 1)
          .some(
            (otherMember) => otherMember.memberEmailId === member.memberEmailId,
          ),
      );

    const hasMemberErrors = orgForm.members.some((member) => {
      const emailHasValue = !!member.memberEmailId;
      const isEmailValid =
        emailHasValue && emailRegex.test(member.memberEmailId);
      const roleMissing = !member.roleId;

      return emailHasValue && (!isEmailValid || roleMissing);
    });

    const hasBulkErrors = createOrgFormBulk.some((entry) => {
      const emailHasValue = !!entry.memberEmailId;
      const isEmailValid =
        emailHasValue && emailRegex.test(entry.memberEmailId);
      const roleMissing = !entry.roleId;

      return emailHasValue && (!isEmailValid || roleMissing);
    });

    return (
      hasOrgNameError ||
      orgForm.zipCodeError ||
      !zipCodeValid ||
      hasDuplicateEmails ||
      hasMemberErrors ||
      hasBulkErrors || // Include bulk errors check
      isNextDisabled
    );
  };

  const handleNext = () => {
    if (!clientOrganization ? !hasErrors() : !clientOrganizationHasErrors())
      setActiveStep((prevActiveStep) =>
        Math.min(prevActiveStep + 1, steps.length - 1),
      );
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => Math.max(prevActiveStep - 1, 0));
  };

  const handleMemberEmailChange = (email, index) => {
    const updatedMembers = [...orgForm.members];
    updatedMembers[index].memberEmailId = email;

    const updatedMemberErrors = [...orgForm.memberError];
    updatedMemberErrors[index] = {
      ...updatedMemberErrors[index],
      // Set error to false if the email is empty, or validate if not empty
      memberEmailIdError: email ? !emailRegex.test(email) : false,
      memberEmailIdErrorMessage: "",
    };

    if (email === emailId) {
      updatedMemberErrors[index] = {
        ...updatedMemberErrors[index],
        memberEmailIdError: true,
        memberEmailIdErrorMessage: "Can't invite yourself",
      };
    } else if (email.includes("@")) {
      if (!emailRegex.test(email)) {
        updatedMemberErrors[index] = {
          ...updatedMemberErrors[index],
          memberEmailIdError: email ? !emailRegex.test(email) : false,
          memberEmailIdErrorMessage:
            email && !emailRegex.test(email) ? "Invalid email format" : "",
        };
      }
    }

    if (index === 0 && email === "") {
      updatedMembers[index].roleId = "";
    }

    // Check for duplicate email only if email is not empty
    const isDuplicate =
      email &&
      orgForm.members.some(
        (invite, i) => invite.memberEmailId === email && i !== index,
      );

    if (isDuplicate) {
      updatedMemberErrors[index].memberEmailIdError = true;
    }

    setOrgForm((prevState) => ({
      ...prevState,
      members: updatedMembers,
      memberError: updatedMemberErrors,
    }));
  };

  const handleRoleChange = (role, index) => {
    const updatedMembers = [...orgForm.members];
    updatedMembers[index].roleId = role;

    const updatedMemberErrors = [...orgForm.memberError];
    updatedMemberErrors[index] = {
      ...updatedMemberErrors[index],
      roleIdError: !role,
    };

    setOrgForm((prevState) => ({
      ...prevState,
      members: updatedMembers,
      memberError: updatedMemberErrors,
    }));
  };

  //add email input box
  const addInviteInput = () => {
    const hasDuplicateEmails =
      orgForm.members?.length > 0 &&
      orgForm.members.some((member, index) =>
        orgForm.members
          .slice(index + 1)
          .some(
            (otherMember) =>
              otherMember.memberEmailId === member.memberEmailId ||
              otherMember.memberEmailId === emailId,
          ),
      );

    const hasEmailErrors = checkAllMemberEmails();

    if (!hasDuplicateEmails && !hasEmailErrors) {
      setOrgForm((prevState) => ({
        ...prevState,
        members: [...prevState.members, { memberEmailId: "", roleId: "" }],
        memberError: [
          ...prevState.memberError,
          { memberEmailIdError: false, roleIdError: false },
        ],
      }));
    }
  };

  // Remove email input box
  const removeInviteInput = (index) => {
    setOrgForm((prevState) => ({
      ...prevState,
      members: prevState.members.filter((_, i) => i !== index),
      memberError: prevState.memberError.filter((_, i) => i !== index),
    }));
  };

  const validateZipCode = (zipCode, country) => {
    if (!zipCode) return;

    let isValid = false;

    // Check validity based on country
    if (country === "USA") {
      const zipData = zipcodes.lookup(zipCode);
      isValid = !!zipData;
    } else if (country === "India") {
      const indiaZipCodeRegex = /^[1-9]{1}[0-9]{5,7}$/;
      isValid = indiaZipCodeRegex.test(zipCode);
    }
    return isValid;
  };

  const flushState = () => {
    setOrgForm({
      orgName: "",
      zipCode: "",
      city: "",
      state: "",
      address: "",
      country: "",
      members: [{}],
      selectOrganization: [],
    });
    setActiveStep(0);
  };

  const prevBulkRef = useRef(createOrgFormBulk?.length);

  // to clear bulk data when members are removed manually from list
  useEffect(() => {
    if (prevBulkRef.current > 0 && createOrgFormBulk?.length === 0) {
      setCreateOrgFormBulk([]);
      setBulkData([]);
      setBulkFile(null);
      setBulkFileName("");
      setBulkFileSize("");
    }
    prevBulkRef.current = createOrgFormBulk?.length;
  }, [createOrgFormBulk]);

  //popups
  const ActivatePopup = (type, message, orgId) => {
    if (type === "success") {
      setPopup({
        popupState: true,
        popupType: "success",
        popupTitle: "Organization Created!",
        popupSubtitle: "Your new organization was set up successfully.",
        popupCancel: true,
        popupCancelTitle: "Go to Org-Overview",
        popupCancelFunction: () => {
          setPopup({ popupState: false });
          navigate(`/account/organizations-overview`);
        },
        popupButton: true,
        popupButtonTitle: "Go to Dashboard",
        popupButtonFunction: () => {
          setPopup({ popupState: false });
          navigate(`/account/${orgId}/dashboard`);
        },
      });
    } else if (type === "error") {
      setPopup({
        popupState: true,
        popupType: "error",
        popupTitle: "Creation Failed",
        popupSubtitle:
          message ||
          "Something went wrong while creating the organization. Please try again.",
        popupCancel: false,
        popupButton: true,
        popupButtonTitle: "Ok",
        popupButtonFunction: () => {
          setPopup({ popupState: false });
          navigate("/account/organizations-overview");
        },
      });
    } else if (type === "none") {
      setPopup({ popupState: false });
    }
  };
  const filteredMembers = orgForm.members.filter(
    (member) => member.memberEmailId && member.roleId,
  );

  // let bulkMembers = [];
  // if (Array.isArray(createOrgFormBulk)) {
  //   bulkMembers = createOrgFormBulk.map((member) => ({
  //     memberEmailId: member.email,
  //     roleId: member.userRole,
  //   }));
  // }
  let domain = window.location.hostname;
  domain =
    domain === "localhost" ? process.env.REACT_APP_METADATA_DOMAIN : domain;

  const triggerBuild = () => {
    if (clientOrganization) {
      const userRole = getRoleInOrg(emailId, selectedOrg);
      if (userRole !== "Owner" && userRole !== "Super Admin") {
        ActivatePopup(
          "error",
          "You are not authorized to create a client for selected organization",
        );
        return;
      }
    }
    setIsLoading(true);
    getStore().dispatch({
      type: Actions.CREATE_ORG,
      orgInfo: {
        orgName: orgForm.orgName,
        ...(clientOrganization ? {} : { zipCode: orgForm.zipCode }),
        country: clientOrganization ? selectedOrgCountry : orgForm.country,
        address: orgForm.address,
        city: orgForm.city,
        state: orgForm.state,
        members:
          filteredMembers.length > 0 && createOrgFormBulk.length > 0
            ? [...filteredMembers, ...createOrgFormBulk]
            : filteredMembers.length > 0
              ? filteredMembers
              : createOrgFormBulk,

        parentOrgId: !clientOrganization
          ? null
          : parseInt(orgForm?.selectOrganization?.value, 10),
        website: false,
        domain: domain,
      },
      meta: {
        onSuccess: (orgId) => {
          setIsLoading(false);
          ActivatePopup("success", null, orgId);
          setBulkData([]);
          setBulkFile(null);
          setBulkFileName("");
          setBulkFileSize("");
        },
        onError: (error) => {
          setIsLoading(false);
          ActivatePopup("error");
          setBulkData([]);
          setBulkFile(null);
          setBulkFileName("");
          setBulkFileSize("");
        },
      },
    });
  };

  const handleOpenBulkPopup = () => {
    setBulkInvite(true);
  };

  const handleCloseBulkPopup = () => {
    setBulkInvite(false);
  };

  const handleBulkInvite = () => {
    if (!bulkInvite || !tempBulkData || tempBulkData.length === 0) {
      return;
    }

    setCreateOrgFormBulk(tempBulkData);

    setOrgForm((prevState) => ({
      ...prevState,
      members: [...prevState.members], // Add the bulk members to existing members
    }));

    handleCloseBulkPopup();
  };

  const handleRemoveAllClick = () => {
    setBulkFile(null);
    setBulkData([]);
    setBulkFileName("");
    setBulkFileSize("");
    setErrors([]);
    setRedundantEmails([]);
    setCreateOrgFormBulk([]);
    setTempBulkData([]);
  };

  const handleBackClick = () => {
    navigate("/account/organizations-overview");
  };

  const returnBack = () => {
    if (isFinished) return;
    if (filteredMembers.length > 0) {
      setOrgForm({
        ...orgForm,
        members: filteredMembers,
      });
    }
    handleBack();
  };

  const validateStep0 = () =>
    clientOrganization
      ? orgForm.orgName &&
        !orgForm.orgNameError &&
        orgForm.selectOrganization &&
        orgForm.selectOrganization.value
      : orgForm.orgName &&
        !orgForm.orgNameError &&
        orgForm.address &&
        !orgForm.addressError &&
        orgForm.city &&
        !orgForm.cityError &&
        orgForm.state &&
        !orgForm.stateError &&
        orgForm.zipCode &&
        !orgForm.zipCodeError &&
        orgForm.country;

  return (
    <>
      <Grid2 className={styles.mainContainer}>
        <Grid2 className={styles.breadcrumbs}>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator={<div className={styles.separatorColor}>/</div>}
          >
            <Grid2 className={styles.options} onClick={handleBackClick}>
              <CorporateFareIcon />
              <Grid2 style={{ marginLeft: "5px" }}>Home</Grid2>
            </Grid2>
            <Link underline="hover" color="inherit" onClick={onBackToOptions}>
              <Grid2 className={styles.options}>Organization Type</Grid2>
            </Link>
            <Grid2 className={styles.activeOption}>
              {!clientOrganization ? "Create Organization" : "Create Client"}
            </Grid2>
          </Breadcrumbs>
        </Grid2>
        <Grid2 className={styles.labelContainer}>
          <Grid2 className={styles.createlabel}>
            {!clientOrganization
              ? "Create Organization"
              : "Create Client Organization"}
          </Grid2>
          <Grid2 className={styles.subLabel}>
            Begin creating your organization in two simple steps.
          </Grid2>
        </Grid2>
        <Grid2 style={{ width: "90%" }}>
          <CustomizedSteppers
            steps={steps}
            activeStep={activeStep}
            userDetails={userDetails}
          />
        </Grid2>
        <Grid2 className={styles.containerBox}>
          <Grid2 className={styles.container}>
            {activeStep === 0 && (
              <>
                <Grid2>
                  <Grid2 style={{ width: "100%" }}>
                    {clientOrganization ? (
                      <Dropdown
                        title={"Select Organization"}
                        subtitle="Choose a licensed organization from the list below."
                        tooltip="Only licensed organizations are available for selection."
                        options={childOrgData
                          // .filter((org) => org.isDedicated !== true)
                          .map((org) => ({
                            label: org.name,
                            value: org.orgId,
                          }))}
                        placeholder="Select an option"
                        value={orgForm.selectOrganization.value}
                        onSelect={(option) => {
                          setOrgForm((prevState) => ({
                            ...prevState,
                            selectOrganization: {
                              value: option,
                              label: orgData.find((org) => org.orgId === option)
                                ?.name,
                            },
                          }));
                        }}
                        dropdownHeader={styles.dropdownList}
                      />
                    ) : null}
                  </Grid2>
                  <Grid2 style={{ width: "100%" }}>
                    <CustomInput
                      title={"Organization Name"}
                      SubTitle="Legal name of your organization."
                      placeholder={"Enter organization name"}
                      value={orgForm.orgName.toLowerCase()}
                      setValue={(e) => {
                        const newValue = e.replace(/[^a-zA-Z0-9]/g, "");
                        if (newValue.length <= 25) {
                          setOrgForm({
                            ...orgForm,
                            orgName: newValue,
                            orgNameError:
                              newValue?.length === 25 || newValue?.length < 3,
                            orgNameErrorMessage:
                              (newValue?.length === 25
                                ? "Max characters are 24"
                                : "") ||
                              (newValue?.length < 3
                                ? "Minimum characters are 3"
                                : ""),
                          });
                        }
                      }}
                      nameLoading={nameLoading}
                      suffix={
                        orgForm.orgName !== "" &&
                        (orgForm.orgName.length > 0 &&
                        orgForm.orgName.length < 25 ? (
                          nameLoading ? (
                            <CircularProgress size={20} />
                          ) : nameValid ? (
                            <CheckCircleOutlineOutlinedIcon
                              style={{ color: "green" }}
                            />
                          ) : nameValid === false ? (
                            <HighlightOffIcon style={{ color: "red" }} />
                          ) : null
                        ) : (
                          <HighlightOffIcon style={{ color: "red" }} />
                        ))
                      }
                      error={orgForm.orgNameError}
                      errorMessage={orgForm.orgNameErrorMessage}
                    />
                  </Grid2>
                </Grid2>

                {!clientOrganization ? (
                  <Grid2 className={styles.address}>
                    <div className={styles.dropdownPlaceholder}>
                      <div className={DropdownStyles.dropdownTitle}>
                        Organization Address
                      </div>
                      <div className={DropdownStyles.dropdownSubtitle}>
                        For Billing
                      </div>
                    </div>
                  </Grid2>
                ) : null}

                {!clientOrganization ? (
                  <Grid2
                    className={styles.address}
                    style={{ marginTop: 0, paddingTop: 0 }}
                  >
                    <Grid2 className={styles.countryDropdown}>
                      <Dropdown
                        title={"Country of residence"}
                        options={countryOptions}
                        placeholder="Select an option"
                        value={orgForm.country}
                        onSelect={(option) => {
                          setOrgForm((prevState) => ({
                            ...prevState,
                            country: option,
                            address: "",
                            city: "",
                            state: "",
                            zipCode: "",
                            addressError: false,
                            addressErrorMessage: "",
                            cityError: false,
                            cityErrorMessage: "",
                            stateError: false,
                            stateErrorMessage: "",
                            zipCodeError: false,
                          }));
                        }}
                        dropdownHeader={styles.dropdownList}
                      />
                    </Grid2>
                    <Grid2 className={styles.addressInput}>
                      <CustomInput
                        title={"Address"}
                        placeholder="Enter Address Line"
                        value={orgForm.address}
                        setValue={(e) => {
                          const newValue = e;
                          if (/^\s/.test(newValue)) {
                            setOrgForm({
                              ...orgForm,
                              address: "",
                              addressError: true,
                              addressErrorMessage:
                                "Address must not start with a space",
                            });
                          } else if (/^[^a-zA-Z0-9\s]/.test(newValue)) {
                            setOrgForm({
                              ...orgForm,
                              address: "",
                              addressError: true,
                              addressErrorMessage:
                                "Address must not start with a special character",
                            });
                          } else if (newValue.length <= 100) {
                            setOrgForm({
                              ...orgForm,
                              address: newValue,
                              addressError: newValue.length < 3,
                              addressErrorMessage:
                                newValue.length < 3
                                  ? "Minimum characters are 3"
                                  : "",
                            });
                          }
                        }}
                        error={orgForm.addressError}
                        errorMessage={orgForm.addressErrorMessage}
                      />
                    </Grid2>
                  </Grid2>
                ) : null}

                {!clientOrganization ? (
                  <Grid2 className={styles.address}>
                    <Grid2 className={styles.addressInput}>
                      <CustomInput
                        title={"City"}
                        placeholder="Enter City"
                        value={orgForm.city}
                        setValue={(e) => {
                          const newValue = e.replace(/[^a-zA-Z\s]/g, "");
                          if (newValue.length <= 100) {
                            setOrgForm({
                              ...orgForm,
                              city: newValue,
                              cityError: newValue?.length < 3,
                              cityErrorMessage:
                                newValue?.length < 3
                                  ? "Minimum characters are 3"
                                  : "",
                            });
                          }
                        }}
                        error={orgForm.cityError}
                        errorMessage={orgForm.cityErrorMessage}
                      />
                    </Grid2>
                    <Grid2 className={styles.addressInput}>
                      <CustomInput
                        title={"State"}
                        placeholder="Enter State"
                        value={orgForm.state}
                        setValue={(e) => {
                          const newValue = e.replace(/[^a-zA-Z\s]/g, "");
                          if (newValue.length <= 100) {
                            setOrgForm({
                              ...orgForm,
                              state: newValue,
                              stateError: newValue?.length < 3,
                              stateErrorMessage:
                                newValue?.length < 3
                                  ? "Minimum characters are 3"
                                  : "",
                            });
                          }
                        }}
                        error={orgForm.stateError}
                        errorMessage={orgForm.stateErrorMessage}
                      />
                    </Grid2>
                    <Grid2 className={styles.zipCode}>
                      <CustomInput
                        title={"Zip Code"}
                        placeholder={"Enter Zip code"}
                        value={orgForm.zipCode}
                        setValue={(e) => {
                          if (!isNaN(e) && e.length <= 10) {
                            setOrgForm((prevState) => {
                              const isValid = validateZipCode(
                                e,
                                orgForm.country,
                              );

                              return {
                                ...prevState,
                                zipCode: e.replace(/\s+/g, ""),
                                zipCodeError: !isValid,
                              };
                            });
                          }
                        }}
                      />
                    </Grid2>
                  </Grid2>
                ) : null}
              </>
            )}
            {activeStep === 1 && (
              <>
                {orgForm.members.map((invite, index) => {
                  const memberValue = invite.memberEmailId.toLowerCase() || "";
                  const roleValue = invite.roleId || "";

                  return (
                    <Grid2 className={styles.inviteContainer} key={index}>
                      <CustomInput
                        title={index === 0 ? "Invite Members" : ""}
                        SubTitle={
                          index === 0
                            ? "Get your projects up and running faster by inviting your team to collaborate."
                            : ""
                        }
                        placeholder={"example@example.com"}
                        value={memberValue}
                        setValue={(option) =>
                          handleMemberEmailChange(
                            option.replace(/\s+/g, ""),
                            index,
                          )
                        }
                        error={orgForm.memberError[index]?.memberEmailIdError}
                        errorMessage={
                          orgForm.memberError[index]?.memberEmailIdErrorMessage
                        }
                      />

                      <Grid2 className={styles.dropdown}>
                        <Dropdown
                          title={index === 0 ? "Select Role" : ""}
                          subtitle={
                            index === 0 ? "This will be the user role" : ""
                          }
                          options={roleOptions}
                          placeholder="Select an option"
                          value={roleValue}
                          onSelect={(option) => handleRoleChange(option, index)}
                          dropdownHeader={styles.dropdownList}
                          disabled={
                            memberValue === "" ||
                            orgForm.memberError[index]?.memberEmailIdError
                          }
                        />

                        {index === orgForm.members.length - 1 ? (
                          <>
                            <Grid2
                              className={`${styles.deleteIconContainer} ${
                                index !== 0
                                  ? styles.deleteIconContainerAdjusted
                                  : ""
                              }`}
                            >
                              <PersonAddAlt1Icon
                                onClick={
                                  !orgForm.memberError[index]
                                    ?.memberEmailIdError &&
                                  memberValue &&
                                  roleValue
                                    ? addInviteInput
                                    : undefined
                                }
                                className={
                                  memberValue && roleValue
                                    ? styles.addIconEnabled
                                    : styles.addIconDisabled
                                }
                              />
                            </Grid2>
                            {orgForm.members.length > 1 && ( // Show delete only if more than one member
                              <Grid2
                                className={`${styles.deleteIconContainer} ${
                                  index !== 0
                                    ? styles.deleteIconContainerAdjusted
                                    : ""
                                }`}
                              >
                                <DeleteIcon
                                  className={styles.deleteIcon}
                                  onClick={() => removeInviteInput(index)}
                                />
                              </Grid2>
                            )}
                          </>
                        ) : (
                          // Delete Icon for non-last indexes
                          <Grid2
                            className={`${styles.deleteIconContainer} ${
                              index !== 0
                                ? styles.deleteIconContainerAdjusted
                                : ""
                            }`}
                          >
                            <DeleteIcon
                              className={styles.deleteIcon}
                              onClick={() => removeInviteInput(index)}
                            />
                          </Grid2>
                        )}
                      </Grid2>
                    </Grid2>
                  );
                })}
                <Grid2 className={styles.bulkInviteText}>
                  {redundantEmails?.length > 0 ? (
                    <span
                      className={styles.errorText}
                      onClick={handleOpenBulkPopup}
                    >
                      The uploaded file is not in given format
                    </span>
                  ) : createOrgFormBulk && createOrgFormBulk?.length === 0 ? (
                    <span
                      className={styles.inviteText}
                      onClick={handleOpenBulkPopup}
                    >
                      Or import a list from a CSV file
                    </span>
                  ) : (
                    <span
                      className={styles.inviteText}
                      onClick={handleOpenBulkPopup}
                    >
                      You're inviting {createOrgFormBulk?.length} members
                    </span>
                  )}
                  {/* {createOrgFormBulk && createOrgFormBulk?.length === 0 ? (
                    <span
                      className={styles.inviteText}
                      onClick={handleOpenBulkPopup}
                    >
                      Or import a list from a CSV file
                    </span>
                  ) : (
                    <span
                      className={styles.inviteText}
                      onClick={handleOpenBulkPopup}
                    >
                      You're inviting {createOrgFormBulk?.length} members
                    </span>
                  )} */}
                </Grid2>
              </>
            )}
            {activeStep === 2 && (
              <OrgReview
                orgForm={orgForm}
                clientOrganization={clientOrganization}
                createOrgFormBulk={createOrgFormBulk}
              />
            )}
          </Grid2>
          <Grid2 className={styles.nextBtnContainer}>
            <hr className={styles.line} />
            <Grid2 className={styles.buttonsContainer}>
              {activeStep > 0 ? (
                <Grid2
                  className={styles.cancelButton}
                  onClick={() => {
                    returnBack();
                  }}
                  style={{
                    opacity: isFinished ? "0.5" : "1",
                    cursor: isFinished ? "not-allowed" : "pointer",
                  }}
                >
                  Back
                </Grid2>
              ) : (
                <Grid2
                  className={styles.cancelButton}
                  onClick={() => {
                    flushState();
                    navigate("/account/organizations-overview");
                  }}
                >
                  Cancel
                </Grid2>
              )}
              {activeStep === steps?.length - 1 ? (
                <Grid2
                  className={styles.nextOrFinishBtn}
                  onClick={() => {
                    if (!isloading) {
                      setIsLoading(true);
                      triggerBuild();
                      setIsFinished(true);
                    }
                  }}
                  style={{ cursor: isloading ? "not-allowed" : "pointer" }}
                >
                  {!isloading ? "Finish" : <CircularProgress size={24} />}
                </Grid2>
              ) : activeStep === 1 ? (
                (() => {
                  const validMembers = orgForm.members?.filter(
                    (m) => m.memberEmailId !== "" && m.roleId !== "",
                  );
                  const members =
                    validMembers?.length > 0 ? validMembers : createOrgFormBulk;
                  const hasMembers = members.length > 0;

                  return (
                    <Grid2
                      className={styles.nextOrFinishBtn}
                      onClick={() => {
                        if (hasMembers) {
                          handleNext();
                        } else {
                          setActiveStep(activeStep + 1);
                        }
                      }}
                      style={{
                        cursor: "pointer",
                        opacity: hasMembers
                          ? (
                              clientOrganization
                                ? clientOrganizationHasErrors()
                                : hasErrors()
                            )
                            ? 0.5
                            : 1
                          : 1,
                      }}
                    >
                      {hasMembers ? "Next" : "Skip"}
                    </Grid2>
                  );
                })()
              ) : (
                <Grid2
                  className={styles.nextOrFinishBtn}
                  onClick={handleNext}
                  style={{
                    opacity: validateStep0() ? 1 : 0.5,
                    cursor: validateStep0() ? "pointer" : "not-allowed",
                  }}
                >
                  Next
                </Grid2>
              )}
              {activeStep === 3 && <ProcessingPage />}
            </Grid2>
          </Grid2>
        </Grid2>
      </Grid2>
      <section>
        <NewPopup
          trigger={bulkInvite}
          closePopup={handleCloseBulkPopup}
          header={"Invite Bulk Users"}
          customWidth={"45%"}
          nextpopup={"Invite"}
          nextOnClick={handleBulkInvite}
          disabled={isBulkDisabled}
        >
          <BulkInvite
            createOrgFormBulk={tempBulkData}
            setCreateOrgFormBulk={setTempBulkData}
            bulkData={bulkData}
            setBulkData={setBulkData}
            bulkFile={bulkFile}
            setBulkFile={setBulkFile}
            bulkFileName={bulkFileName}
            setBulkFileName={setBulkFileName}
            bulkFileSize={bulkFileSize}
            setBulkFileSize={setBulkFileSize}
            redundantEmails={redundantEmails} // Pass redundantEmails as a prop
            setRedundantEmails={setRedundantEmails}
            handleRemoveAllClick={handleRemoveAllClick}
            errors={errors}
            setErrors={setErrors}
          />
        </NewPopup>
      </section>
      <section>
        <FormPopup
          trigger={popup.popupState}
          setTrigger={setPopup}
          type={popup.popupType}
          title={popup.popupTitle}
          subtitle={popup.popupSubtitle}
          button={popup.popupButton}
          cancel={popup.popupCancel}
          cancelTitle={popup.popupCancelTitle}
          cancelFunction={popup.popupCancelFunction}
          buttonTitle={popup.popupButtonTitle}
          buttonFunction={popup.popupButtonFunction}
        />
      </section>
    </>
  );
}

export function OrgReview({ orgForm, clientOrganization, createOrgFormBulk }) {
  const validMembers = [
    ...(orgForm.members?.filter(
      (invite) => invite.memberEmailId && invite.roleId,
    ) || []),
    ...(createOrgFormBulk?.filter(
      (invite) => invite.memberEmailId && invite.roleId,
    ) || []),
  ];

  return (
    <Grid2 className={styles.reviewCardContainer}>
      <Grid2 className={styles.reviewCard}>
        {clientOrganization && (
          <ReviewCard
            header="Selected Organization"
            subTitle="Managing Organization"
            title={orgForm.selectOrganization?.label}
            icon={TempIcon}
          />
        )}
        <ReviewCard
          header="Organization Name"
          subTitle="Organization Name"
          title={orgForm.orgName}
          icon={TempIcon}
        />
        {!clientOrganization && (
          <>
            <ReviewCard
              header="Zip Code Info"
              subTitle="Zip Code"
              title={orgForm.zipCode}
              icon={TempIcon}
            />
            <ReviewCard
              header="Country Info"
              subTitle="Country"
              title={orgForm.country}
              icon={TempIcon}
            />
          </>
        )}

        {validMembers?.length > 0 && (
          <ReviewCard
            header="Role Info"
            subTitle="No.of Users"
            title={validMembers?.length || 0}
            icon={TempIcon}
          />
        )}
      </Grid2>

      {validMembers?.length > 0 ? (
        <>
          <Grid2 className={styles.createlabel}>Invited Users</Grid2>
          <Grid2 className={styles.reviewMembersCard} spacing={2}>
            {validMembers.map((invite, index) => (
              <Grid2
                container
                key={index}
                spacing={2}
                justifyContent={"center"}
              >
                <Grid2 xs={12} sm={6}>
                  <ReviewField
                    label={index === 0 ? `No.` : ""}
                    value={index + 1}
                    showUnderscore={false}
                  />
                </Grid2>
                <Grid2 xs={12} sm={6} className={styles.memberRoleWidth}>
                  <ReviewField
                    label={index === 0 ? `Member` : ""}
                    value={invite.memberEmailId}
                    showUnderscore={false}
                  />
                </Grid2>
                <Grid2 xs={12} sm={6} className={styles.memberRoleWidth}>
                  <ReviewField
                    label={index === 0 ? `Role` : ""}
                    value={getRoleDisplayText(invite.roleId)}
                    showUnderscore={false}
                  />
                </Grid2>
              </Grid2>
            ))}
          </Grid2>
        </>
      ) : (
        <Grid2 className={styles.noInvites}>No Invited Users</Grid2>
      )}
    </Grid2>
  );
}
